import React, { useMemo, useState } from "react";
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useColorModeValue,
  Box,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from "@chakra-ui/react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { FiMoreVertical, FiFilter } from "react-icons/fi";
import { useSelector, useDispatch } from 'react-redux';
import { updateInvoiceStatus } from 'features/invoices/actions';
import StatusUpdateModal from 'components/modals/StatusUpdateModal';
import { useToastNotification } from 'utils/toast-utils';
import { EditIcon } from "@chakra-ui/icons";
import FilterDrawer from "./FilterDrawer";

const InvoicesTable = ({ columnsData, tableData, activeTab, setActiveTab, onRowClick, onEditDraft, onFilterApply }) => {
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    Approved: false,
    Under_Review: false,
    Rejected: false,
    Funded: false,
    Incomplete: false,
    Closed: false,
    Deleted: false
  });
  const [appliedFilters, setAppliedFilters] = useState({
    Approved: false,
    Under_Review: false,
    Rejected: false,
    Funded: false,
    Incomplete: false,
    Closed: false,
    Deleted: false
  });

  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const { clients, clientsLoading } = useSelector((state) => state.users);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = tableInstance;

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return 'gray.500';
    
    switch (normalizedStatus) {
      case 'PENDING':
        return 'orange.500';
      case 'UNDER REVIEW':
        return 'purple.500';
      case 'APPROVED':
        return 'green.500';
      case 'REJECTED':
        return 'red.500';
      case 'FUNDED':
        return 'teal.500';
      case 'INCOMPLETE':
        return 'yellow.500';
      case 'DELETED':
        return 'gray.500';
      case 'CLOSED':
        return 'blue.500';
      default:
        return 'gray.500';
    }
  };

  const getStatusBgColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return 'gray.100';
    
    switch (normalizedStatus) {
      case 'PENDING':
        return 'orange.100';
      case 'UNDER REVIEW':
        return 'purple.100';
      case 'APPROVED':
        return 'green.100';
      case 'REJECTED':
        return 'red.100';
      case 'FUNDED':
        return 'teal.100';
      case 'INCOMPLETE':
        return 'yellow.100';
      case 'DELETED':
        return 'gray.100';
      case 'CLOSED':
        return 'blue.100';
      default:
        return 'gray.100';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleStatusUpdate = async (status, reason = '') => {
    try {
      if (status === 'Closed' && !canInvoiceBeClosed(selectedInvoice)) {
        showToast('Error', 'Invoice must be fully paid before closing', 'error');
        setIsStatusModalOpen(false);
        return;
      }

      setLoading(true);
      await dispatch(updateInvoiceStatus({
        invoiceId: selectedInvoice._id,
        status,
        reason
      })).unwrap();
      
      showToast('Success', 'Invoice status updated successfully', 'success');
      setIsStatusModalOpen(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to update status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuItemClick = (invoice, status) => {
    setSelectedInvoice(invoice);
    setSelectedStatus(status);
    setIsStatusModalOpen(true);
  };

  const handleApply = (selectedStatuses) => {
    const newAppliedFilters = { ...filters };
    setAppliedFilters(newAppliedFilters);
    onFilterApply(selectedStatuses);
    setIsFilterOpen(false);
  };

  const handleCancel = () => {
    setFilters({
      Approved: false,
      Under_Review: false,
      Rejected: false,
      Funded: false,
      Incomplete: false,
      Closed: false,
      Deleted: false
    });
    setAppliedFilters({
      Approved: false,
      Under_Review: false,
      Rejected: false,
      Funded: false,
      Incomplete: false,
      Closed: false,
      Deleted: false
    });
    onFilterApply([]);
    setIsFilterOpen(false);
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'Approved';
      case 'UNDER REVIEW':
        return 'Under Review';
      case 'REJECTED':
        return 'Rejected';
      case 'FUNDED':
        return 'Funded';
      case 'INCOMPLETE':
        return 'Incomplete';
      case 'CLOSED':
        return 'Closed';
      case 'DELETED':
        return 'Deleted';
      case 'PENDING':
        return 'Pending';
      case 'DRAFT':
        return 'Draft';
      default:
        return status;
    }
  };

  const handleRemoveFilter = (status) => {
    const newFilters = { ...appliedFilters, [status]: false };
    setAppliedFilters(newFilters);
    setFilters(newFilters);
    const activeFilters = Object.entries(newFilters)
      .filter(([_, isActive]) => isActive)
      .map(([status]) => status);
    onFilterApply(activeFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(appliedFilters).filter(Boolean).length;
  };

  const canInvoiceBeClosed = (invoice) => {
    return invoice.remainingAmount === 0;
  };

  return (
    <Box>
      <Flex mb={4} position="sticky" top="0" bg="white" align="center">
        {["All", "Draft"].map((tab) => (
          <Button
            key={tab}
            variant="ghost"
            bg={activeTab === tab ? "purple.50" : "transparent"}
            color={activeTab === tab ? "purple.500" : "gray.500"}
            onClick={() => setActiveTab(tab)}
            borderRadius="4px"
            mr={2}
            height="36px"
            fontSize="14px"
            fontWeight="500"
            _hover={{
              bg: activeTab === tab ? "purple.50" : "gray.50"
            }}
            _active={{
              bg: activeTab === tab ? "purple.50" : "gray.100"
            }}
          >
            {tab}
          </Button>
        ))}
        {activeTab !== "Draft" && (
          <Button
            leftIcon={<FiFilter />}
            variant={getActiveFilterCount() > 0 ? "solid" : "outline"}
            ml="auto"
            borderRadius="8px"
            fontWeight="500"
            size="md"
            px={4}
            py={2}
            color={getActiveFilterCount() > 0 ? "white" : "gray.600"}
            bg={getActiveFilterCount() > 0 ? "purple.500" : "white"}
            borderColor={getActiveFilterCount() > 0 ? "purple.500" : "gray.200"}
            boxShadow="sm"
            onClick={() => setIsFilterOpen(true)}
            _hover={{
              bg: getActiveFilterCount() > 0 ? "purple.600" : "gray.50",
              borderColor: getActiveFilterCount() > 0 ? "purple.600" : "gray.300"
            }}
            _active={{
              bg: getActiveFilterCount() > 0 ? "purple.700" : "gray.100"
            }}
            position="relative"
          >
            Filter By
            {getActiveFilterCount() > 0 && (
              <Flex
                position="absolute"
                top="-8px"
                right="-8px"
                bg="red.500"
                color="white"
                borderRadius="full"
                minW="20px"
                h="20px"
                align="center"
                justify="center"
                fontSize="xs"
                fontWeight="bold"
                boxShadow="sm"
              >
                {getActiveFilterCount()}
              </Flex>
            )}
          </Button>
        )}
      </Flex>
      
      {activeTab !== "Draft" && Object.entries(appliedFilters).some(([_, isActive]) => isActive) && (
        <Flex mb={4} gap={2} flexWrap="wrap">
          {Object.entries(appliedFilters).map(([status, isActive]) => {
            if (!isActive) return null;
            return (
              <Flex
                key={status}
                align="center"
                bg={getStatusBgColor(status)}
                color={getStatusColor(status)}
                px={3}
                py={1}
                borderRadius="full"
                fontSize="sm"
                fontWeight="500"
                boxShadow="sm"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-1px)",
                  boxShadow: "md"
                }}
              >
                {getStatusDisplayName(status)}
                <Button
                  size="sm"
                  variant="ghost"
                  color={getStatusColor(status)}
                  ml={2}
                  p={0}
                  minW="auto"
                  h="auto"
                  onClick={() => handleRemoveFilter(status)}
                  _hover={{ 
                    bg: 'transparent', 
                    opacity: 0.8,
                    transform: "scale(1.1)"
                  }}
                  transition="all 0.2s"
                >
                  ×
                </Button>
              </Flex>
            );
          })}
        </Flex>
      )}

      <Box>
        <Table {...getTableProps()} variant="simple">
          <Thead position="sticky" top="0" bg="white">
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps()}
                    pe="10px"
                    key={index}
                    borderColor={borderColor}
                    whiteSpace="nowrap"
                  >
                    <Text
                      color="gray.600"
                      fontSize="12px"
                      fontWeight="700"
                      whiteSpace="nowrap"
                    >
                      {column.render("Header")}
                    </Text>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <Tr 
                  {...row.getRowProps()} 
                  key={index}
                  onClick={(e) => {
                    if (!e.defaultPrevented && activeTab !== "Draft") {
                      onRowClick(row.original);
                    }
                  }}
                  cursor={activeTab === "Draft" ? "default" : "pointer"}
                  _hover={{
                    bg: activeTab === "Draft" ? "transparent" : "gray.50"
                  }}
                >
                  {row.cells.map((cell, index) => {
                    let data = cell.value;
                    
                    if (cell.column.Header === "Status") {
                      data = (
                        <Text
                          px={3}
                          py={1}
                          borderRadius="full"
                          bg={getStatusBgColor(cell.value)}
                          color={getStatusColor(cell.value)}
                          fontSize="sm"
                          fontWeight="500"
                          display="inline-block"
                          whiteSpace="nowrap"
                          width="fit-content"
                        >
                          {getStatusDisplayName(cell.value)}
                        </Text>
                      );
                    } else if (cell.column.Header === "Company Name") {
                      const client = clients.find(client => client._id === cell.value);
                      data = client ? client.companyName : 'N/A';
                    } else if (cell.column.Header === "Actions") {
                      data = activeTab === "Draft" ? (
                        <Flex justify="center">
                          <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<EditIcon />}
                            color="purple.500"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditDraft(row.original);
                            }}
                            _hover={{
                              bg: "purple.50"
                            }}
                            aria-label="Edit Draft Invoice"
                          >
                            Edit Draft
                          </Button>
                        </Flex>
                      ) : (
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                            isDisabled={row.original.status === 'CLOSED'}
                          />
                          <MenuList>
                            <MenuItem 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Approved');
                              }}
                              isDisabled={row.original.status === 'Closed'}
                            >
                              Approve For Funding
                            </MenuItem>
                            <MenuItem 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Under Review');
                              }}
                              isDisabled={row.original.status === 'Closed'}
                            >
                              Under Review
                            </MenuItem>
                            <MenuItem 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Rejected');
                              }}
                              isDisabled={row.original.status === 'Closed'}
                            >
                              Rejected
                            </MenuItem>
                            <MenuItem 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Funded');
                              }}
                              isDisabled={row.original.status === 'Closed' || !row.original.termId}
                              title={!row.original.termId ? "Invoice must have a term ID to be funded" : ""}
                            >
                              Funded
                            </MenuItem>
                            <MenuItem 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Incomplete');
                              }}
                              isDisabled={row.original.status === 'Closed'}
                            >
                              Additional Document Required
                            </MenuItem>
                            <MenuItem 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Closed');
                              }}
                              isDisabled={row.original.status === 'Closed' || !canInvoiceBeClosed(row.original)}
                              title={!canInvoiceBeClosed(row.original) ? "Invoice must be fully paid before closing" : ""}
                            >
                              Closed
                            </MenuItem>
                            <MenuItem 
                              color="red.500" 
                              onClick={(e) => {
                                e.preventDefault();
                                handleMenuItemClick(row.original, 'Deleted');
                              }}
                              isDisabled={row.original.status === 'Closed'}
                            >
                              Delete Invoice
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      );
                    } else if (cell.column.Header === "Created On" || cell.column.Header === "Expected Due Date") {
                      data = formatDate(cell.value);
                    } else if (cell.column.Header === "USD Amount") {
                      data = formatCurrency(cell.value);
                    } else {
                      data = cell.value || 'N/A';
                    }

                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        minW={{ sm: "150px", md: "200px", lg: "auto" }}
                        borderColor={borderColor}
                      >
                        {data}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusUpdate}
        status={selectedStatus}
        loading={loading}
      />

      <FilterDrawer
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApply}
        onCancel={handleCancel}
      />
    </Box>
  );
};

export default InvoicesTable;
